import { queryOptions } from "@tanstack/react-query";
import type { UserService } from "@/services/user.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The signed-in user's notification switches in one workspace (NG-2814),
 * every category with its in-app and Teams answers,
 * `[slug, uid, 'notification-settings']`. Mutable: it is the screen where
 * the person flips them. A plain GET, so `"safe-read"`.
 *
 * @writers `UserStore.updateNotificationSetting` and `updateTeamsSetting` flip
 * the switch in the entry at once, put the list back on failure, and replace
 * it with the server's answer, which carries every category's resolved value.
 */

/** The notification settings key. */
export function notificationSettingsKey(scope: QueryScope) {
  return scopedKey(scope, "notification-settings");
}

/** The signed-in user's notification settings in `scope`'s workspace. */
export function notificationSettingsQuery(
  service: Pick<UserService, "notificationSettings">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: notificationSettingsKey(scope),
    queryFn: scopedRead(
      async (request) => (await service.notificationSettings(scope.tenant, request)).settings,
      { retry: "safe-read" },
    ),
  });
}
